import { findAttackableTarget } from "../targeting";
import type { FlightMovement } from "@frontier/content/combat-rules";
import {
  movementRegionBounds,
  pilotMovementObstacles,
  integrateMovement,
  updateMovementEnergy,
} from "@frontier/simulation/movement";
import type { MovementBody } from "@frontier/simulation/movement";
import type { ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";
import type { InputIntent } from "./input";

const ENERGY_STEP_SECONDS = 1 / 20;
const MAX_PREDICTION_AGE_MS = 250;
const TELEPORT_DISTANCE = 220;
const CORRECTION_RATE = 14;

export interface LocalPose extends MovementBody {
  boosting: boolean;
  moving: boolean;
}

/** Visual movement only: authoritative combat/economy never enters this state. */
export class LocalMovement {
  private pose: LocalPose = {
    x: 0,
    y: 0,
    angle: 0,
    radius: 22,
    boosting: false,
    moving: false,
  };
  private authoritative: ShipSnapshot | undefined;
  private receivedAt = 0;
  private energy = 0;
  private energyRemainder = 0;
  private correction = { x: 0, y: 0, angle: 0 };

  reset(): void {
    this.authoritative = undefined;
  }

  receive(ship: ShipSnapshot, now: number): void {
    const previous = this.authoritative;
    const reset =
      !previous ||
      !ship.controls ||
      ship.dead ||
      ship.docked ||
      previous.dead ||
      previous.docked ||
      ship.id !== previous.id ||
      ship.hullId !== previous.hullId ||
      JSON.stringify(ship.controls) !== JSON.stringify(previous.controls) ||
      Math.hypot(ship.x - this.pose.x, ship.y - this.pose.y) >
        TELEPORT_DISTANCE;
    this.authoritative = ship;
    this.receivedAt = now;
    this.energy = ship.energy;
    this.pose.radius = ship.radius;
    if (reset) {
      this.pose = {
        x: ship.x,
        y: ship.y,
        angle: ship.angle,
        radius: ship.radius,
        boosting: false,
        moving: false,
      };
      this.correction = { x: 0, y: 0, angle: 0 };
      this.energyRemainder = 0;
    } else {
      this.correction.x = ship.x - this.pose.x;
      this.correction.y = ship.y - this.pose.y;
      this.correction.angle = Math.atan2(
        Math.sin(ship.angle - this.pose.angle),
        Math.cos(ship.angle - this.pose.angle),
      );
    }
  }

  frame(
    world: WorldSnapshot,
    input: InputIntent,
    now: number,
    deltaSeconds: number,
    intro: boolean,
    friends: readonly string[] = [],
  ): LocalPose | undefined {
    const ship = this.authoritative;
    if (!ship) return undefined;
    const dt = Math.max(0, Math.min(0.05, deltaSeconds));
    const hull = ship.controls?.movement;
    if (!hull) return this.pose;
    const active =
      !ship.dead &&
      !ship.docked &&
      now - this.receivedAt <= MAX_PREDICTION_AGE_MS;
    const mx = active ? input.mx : 0;
    const my = active ? input.my : 0;
    const length = Math.hypot(mx, my);
    const boostIntent = active && input.boost;
    // The energy gate uses the server's 50 ms step even when rendering at 60 Hz.
    this.pose.boosting = updateMovementEnergy(
      this.energy,
      ship.maxEnergy,
      boostIntent,
      length,
      ENERGY_STEP_SECONDS,
      hull.boostEnergyFactor,
      hull.rules,
    ).boosting;
    this.pose.moving = length > 0;
    this.energyRemainder += dt;
    while (this.energyRemainder >= ENERGY_STEP_SECONDS) {
      this.energy = updateMovementEnergy(
        this.energy,
        ship.maxEnergy,
        boostIntent,
        length,
        ENERGY_STEP_SECONDS,
        hull.boostEnergyFactor,
        hull.rules,
      ).energy;
      this.energyRemainder -= ENERGY_STEP_SECONDS;
    }
    if (ship.dead || ship.docked) return this.pose;
    this.applyCorrection(hull, dt);
    const target = findAttackableTarget(world, ship, input.targetId, friends);
    const aimX = target?.x ?? input.aimX;
    const aimY = target?.y ?? input.aimY;
    const desiredAngle =
      active && Math.hypot(aimX - this.pose.x, aimY - this.pose.y) > 2
        ? Math.atan2(aimY - this.pose.y, aimX - this.pose.x)
        : this.pose.angle;
    integrateMovement(this.pose, {
      mx,
      my,
      desiredAngle,
      boost: this.pose.boosting,
      boostSpeedFactor: hull.boostSpeedFactor,
      rules: hull.rules,
      dt,
      hull,
      obstacles: pilotMovementObstacles(
        world.obstacles,
        [...world.npcs, ...world.players],
        intro,
        ship.id,
        world.boundaryRules ? ship.sectorId : undefined,
      ),
      bounds: movementRegionBounds(this.pose, world.boundaryRules ? ship.sectorId : undefined),
      unbounded: world.boundaryRules !== undefined,
      width: world.width,
      height: world.height,
    });
    return this.pose;
  }

  private applyCorrection(hull: FlightMovement, dt: number): void {
    const fraction = 1 - Math.exp(-CORRECTION_RATE * dt);
    const length = Math.hypot(this.correction.x, this.correction.y);
    const positionFraction =
      length > 0 ? Math.min(fraction, (hull.speed * 0.8 * dt) / length) : 0;
    this.pose.x += this.correction.x * positionFraction;
    this.pose.y += this.correction.y * positionFraction;
    this.correction.x *= 1 - positionFraction;
    this.correction.y *= 1 - positionFraction;
    const angleChange = Math.max(
      -hull.turnRate * dt * 0.35,
      Math.min(hull.turnRate * dt * 0.35, this.correction.angle * fraction),
    );
    this.pose.angle += angleChange;
    this.correction.angle -= angleChange;
  }
}
